import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { apiRequest } from "../../api/http";
import {
  AppPlatform,
  AppUpdateDeleteResponse,
  AppUpdateListResponse,
  AppUpdateRelease,
  AppUpdateUpsertPayload,
} from "../../types/admin";

type AppUpdateState = {
  items: AppUpdateRelease[];
  totalCount: number;
  latestByPlatform: AppUpdateListResponse["latest_by_platform"];
  loading: boolean;
  error: string | null;
  saving: boolean;
  saveError: string | null;
  deleting: boolean;
  deleteError: string | null;
};

const initialState: AppUpdateState = {
  items: [],
  totalCount: 0,
  latestByPlatform: { ios: null, android: null },
  loading: false,
  error: null,
  saving: false,
  saveError: null,
  deleting: false,
  deleteError: null,
};

export const fetchAppUpdates = createAsyncThunk<
  AppUpdateListResponse,
  { platform?: AppPlatform } | void,
  { rejectValue: string }
>("appUpdate/fetchList", async (params, { rejectWithValue }) => {
  try {
    const query = params?.platform ? `?platform=${params.platform}` : "";
    return await apiRequest<AppUpdateListResponse>(`/admin/app-update${query}`);
  } catch (error) {
    const message = error instanceof Error ? error.message : "앱 업데이트 목록 조회에 실패했습니다.";
    return rejectWithValue(message);
  }
});

export const upsertAppUpdate = createAsyncThunk<
  AppUpdateRelease,
  AppUpdateUpsertPayload,
  { rejectValue: string }
>("appUpdate/upsert", async (payload, { rejectWithValue }) => {
  try {
    return await apiRequest<AppUpdateRelease>("/admin/app-update", {
      method: "PUT",
      body: payload,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : "앱 업데이트 저장에 실패했습니다.";
    return rejectWithValue(message);
  }
});

export const deleteAppUpdate = createAsyncThunk<
  AppUpdateDeleteResponse,
  { platform: AppPlatform; version: string },
  { rejectValue: string }
>("appUpdate/delete", async ({ platform, version }, { rejectWithValue }) => {
  try {
    return await apiRequest<AppUpdateDeleteResponse>(
      `/admin/app-update/${platform}/${encodeURIComponent(version)}`,
      { method: "DELETE" }
    );
  } catch (error) {
    const message = error instanceof Error ? error.message : "앱 업데이트 삭제에 실패했습니다.";
    return rejectWithValue(message);
  }
});

const appUpdateSlice = createSlice({
  name: "appUpdate",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppUpdates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAppUpdates.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.totalCount = action.payload.total_count;
        state.latestByPlatform = action.payload.latest_by_platform;
      })
      .addCase(fetchAppUpdates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "앱 업데이트 목록 조회에 실패했습니다.";
      })
      .addCase(upsertAppUpdate.pending, (state) => {
        state.saving = true;
        state.saveError = null;
      })
      .addCase(upsertAppUpdate.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(upsertAppUpdate.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.payload ?? "앱 업데이트 저장에 실패했습니다.";
      })
      .addCase(deleteAppUpdate.pending, (state) => {
        state.deleting = true;
        state.deleteError = null;
      })
      .addCase(deleteAppUpdate.fulfilled, (state) => {
        state.deleting = false;
      })
      .addCase(deleteAppUpdate.rejected, (state, action) => {
        state.deleting = false;
        state.deleteError = action.payload ?? "앱 업데이트 삭제에 실패했습니다.";
      });
  },
});

export default appUpdateSlice.reducer;
